/**
 * data/instrumentCatalog/cfd.ts
 * Contract for Difference (CFD) instrument reference catalog
 * 
 * Contains CFD instruments across major asset categories with proper taxonomy
 * and broker-standardized symbol conventions.
 * 
 * Data source: Multi-broker research across Pepperstone, IG, FXCM, CMC Markets
 * Research file: docs/reference/InstrumentCatalogResearch/CFD_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * CFD instrument catalog
 * 
 * Contains 38 core CFD instruments across major categories:
 * - Stock Index CFDs (18 major indices)
 * - Energy CFDs (5 instruments)
 * - Metals CFDs (5 instruments)  
 * - Equity CFDs (20 representative samples - US/EU)
 * 
 * Note: Equity universe is sampled for schema validation. Full coverage
 * requires programmatic ingestion from broker product feeds.
 */
export const CFD_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== STOCK INDEX CFDS =====
  
  {
    id: 'US500_CFD',
    symbol: 'US500',
    name: 'S&P 500 CFD',
    shortName: 'S&P 500',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.1,
    incrementType: 'point',
    quoteCurrency: 'USD',
    notes: 'Also listed as US SPX 500 / SPX500 / US 500'
  },
  
  {
    id: 'US100_CFD',
    symbol: 'US100',
    name: 'Nasdaq 100 CFD',
    shortName: 'Nasdaq 100',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.1,
    incrementType: 'point',
    quoteCurrency: 'USD',
    notes: 'Also listed as NAS100 / NDX / NASDAQ'
  },

  {
    id: 'US30_CFD',
    symbol: 'US30',
    name: 'Dow Jones Industrial Average CFD',
    shortName: 'Dow Jones',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 1.0,
    incrementType: 'point',
    quoteCurrency: 'USD',
    notes: 'Also listed as DJI / DJIA'
  },

  {
    id: 'UK100_CFD',
    symbol: 'UK100',
    name: 'FTSE 100 CFD',
    shortName: 'FTSE 100',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.5,
    incrementType: 'point',
    quoteCurrency: 'GBP',
    notes: 'Also listed as FTSE / UKX'
  },

  {
    id: 'GER40_CFD',
    symbol: 'GER40',
    name: 'DAX 40 CFD',
    shortName: 'DAX 40',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.5,
    incrementType: 'point',
    quoteCurrency: 'EUR',
    notes: 'Also listed as DAX / DE40'
  },

  {
    id: 'FRA40_CFD',
    symbol: 'FRA40',
    name: 'CAC 40 CFD',
    shortName: 'CAC 40',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.5,
    incrementType: 'point',
    quoteCurrency: 'EUR',
    notes: 'Also listed as CAC / FR40'
  },

  {
    id: 'AUS200_CFD',
    symbol: 'AUS200',
    name: 'ASX 200 CFD',
    shortName: 'ASX 200',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.1,
    incrementType: 'point',
    quoteCurrency: 'AUD',
    notes: 'Also listed as AU200 / ASX200'
  },

  {
    id: 'JPN225_CFD',
    symbol: 'JPN225',
    name: 'Nikkei 225 CFD',
    shortName: 'Nikkei 225',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'indices',
      subAssetClass: 'stock-index'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 1.0,
    incrementType: 'point',
    quoteCurrency: 'JPY',
    notes: 'Also listed as JP225 / N225 / Nikkei'
  },

  // ===== ENERGY CFDS =====

  {
    id: 'BRENT_CFD',
    symbol: 'BRENT',
    name: 'Brent Crude Oil CFD',
    shortName: 'Brent Oil',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'energies',
      subAssetClass: 'crude-oil'
    },
    contractSize: 1,
    contractAsset: 'barrel',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Europe benchmark crude oil'
  },

  {
    id: 'WTI_CFD',
    symbol: 'WTI',
    name: 'West Texas Intermediate Oil CFD',
    shortName: 'WTI Oil',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'energies',
      subAssetClass: 'crude-oil'
    },
    contractSize: 1,
    contractAsset: 'barrel',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US benchmark crude oil'
  },

  {
    id: 'NATGAS_CFD',
    symbol: 'NATGAS',
    name: 'Natural Gas CFD',
    shortName: 'Natural Gas',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'energies',
      subAssetClass: 'natural-gas'
    },
    contractSize: 1,
    contractAsset: 'mmBtu',
    incrementSize: 0.001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Henry Hub natural gas'
  },

  // ===== METALS CFDS =====

  {
    id: 'GOLD_CFD',
    symbol: 'GOLD',
    name: 'Gold CFD',
    shortName: 'Gold',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'metals',
      subAssetClass: 'precious'
    },
    contractSize: 1,
    contractAsset: 'troy oz',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Also listed as XAUUSD / Au'
  },

  {
    id: 'SILVER_CFD',
    symbol: 'SILVER',
    name: 'Silver CFD',
    shortName: 'Silver',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'metals',
      subAssetClass: 'precious'
    },
    contractSize: 1,
    contractAsset: 'troy oz',
    incrementSize: 0.001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Also listed as XAGUSD / Ag'
  },

  {
    id: 'COPPER_CFD',
    symbol: 'COPPER',
    name: 'Copper CFD',
    shortName: 'Copper',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'metals',
      subAssetClass: 'industrial'
    },
    contractSize: 1,
    contractAsset: 'lb',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Also listed as Cu'
  },

  {
    id: 'PLATINUM_CFD',
    symbol: 'PLATINUM',
    name: 'Platinum CFD',
    shortName: 'Platinum',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'metals',
      subAssetClass: 'precious'
    },
    contractSize: 1,
    contractAsset: 'troy oz',
    incrementSize: 0.1,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Also listed as XPTUSD / Pt'
  },

  {
    id: 'PALLADIUM_CFD',
    symbol: 'PALLADIUM',
    name: 'Palladium CFD',
    shortName: 'Palladium',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'metals',
      subAssetClass: 'precious'
    },
    contractSize: 1,
    contractAsset: 'troy oz',
    incrementSize: 0.05,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Also listed as XPDUSD / Pd'
  },

  // ===== EQUITY CFDS (REPRESENTATIVE SAMPLE) =====

  {
    id: 'AAPL_CFD',
    symbol: 'AAPL',
    name: 'Apple Inc. CFD',
    shortName: 'Apple CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on Apple stock'
  },

  {
    id: 'MSFT_CFD',
    symbol: 'MSFT',
    name: 'Microsoft Corp. CFD',
    shortName: 'Microsoft CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on Microsoft stock'
  },

  {
    id: 'NVDA_CFD',
    symbol: 'NVDA',
    name: 'NVIDIA Corp. CFD',
    shortName: 'NVIDIA CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on NVIDIA stock'
  },

  {
    id: 'AMZN_CFD',
    symbol: 'AMZN',
    name: 'Amazon.com Inc. CFD',
    shortName: 'Amazon CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on Amazon stock'
  },

  {
    id: 'GOOGL_CFD',
    symbol: 'GOOGL',
    name: 'Alphabet Inc. Class A CFD',
    shortName: 'Alphabet CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on Alphabet Class A stock'
  },

  {
    id: 'TSLA_CFD',
    symbol: 'TSLA',
    name: 'Tesla Inc. CFD',
    shortName: 'Tesla CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'CFD on Tesla stock'
  },

  // ===== EUROPEAN EQUITY CFDS =====

  {
    id: 'SAP_CFD',
    symbol: 'SAP',
    name: 'SAP SE CFD',
    shortName: 'SAP CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'eu-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'EUR',
    notes: 'CFD on SAP stock (Frankfurt)'
  },

  {
    id: 'ASML_CFD',
    symbol: 'ASML',
    name: 'ASML Holding NV CFD',
    shortName: 'ASML CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'eu-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'EUR',
    notes: 'CFD on ASML stock (Amsterdam)'
  },

  {
    id: 'NESN_CFD',
    symbol: 'NESN',
    name: 'Nestle SA CFD',
    shortName: 'Nestle CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'eu-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'CHF',
    notes: 'CFD on Nestle stock (Zurich)'
  },

  {
    id: 'VOW_CFD',
    symbol: 'VOW',
    name: 'Volkswagen AG CFD',
    shortName: 'Volkswagen CFD',
    classification: {
      assetClass: 'cfd',
      marketSegment: 'eu-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'EUR',
    notes: 'CFD on Volkswagen stock (Frankfurt)'
  }
];